import moment from 'moment-timezone';
import * as React from 'react';

import {
  generateMonths, generateWeeksInMonth, IDropdownItem,
} from '../dateRangePresets';
import DatePickerFilter from '../datePickerComponent';
import {
  endOfDayInTz, IPulsePeriodRange, PulsePeriodType, startOfDayInTz,
} from './pulsePeriod';
import { IPulseRunOptions } from '../../membersService';

import '../../../styles/members/simpleDateRangePickAndRun.scss';
import '../../../styles/members/pulsePeriodPicker.scss';

interface IProps {
  timezone: string;
  disabled?: boolean;
  /** Hides the test-users toggle when the caller lacks the permission. */
  canShowTestUsers?: boolean;
  onGenerate: (range: IPulsePeriodRange, runOptions: IPulseRunOptions) => void;
}

interface IState {
  periodType: PulsePeriodType;
  /** Monthly mode: index into the month dropdowns. Weekly mode: month the weeks belong to. */
  monthIndex: string;
  from: string;
  to: string;
  /** Eligibility toggles; both false = the report defaults (exclude test users, exclude un-onboarded). */
  includeTestUsers: boolean;
  includeUnonboarded: boolean;
}

const PULSE_PERIOD_TYPES: Array<{ type: PulsePeriodType; label: string }> = [
  { type: 'weeks', label: 'Weekly' },
  { type: 'months', label: 'Monthly' },
  { type: 'custom', label: 'Custom' },
];

/**
 * Weekly / Monthly / Custom range selector with a Generate button for the
 * Monthly Pulse modal. The period type is a joined toggle group (one active
 * at a time); the Stats-style dropdowns cover weeks/months and custom uses
 * the shared DatePickerFilter.
 */
export default class PulsePeriodPicker extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      periodType: 'months',
      monthIndex: '0',
      from: '0',
      to: '0',
      includeTestUsers: false,
      includeUnonboarded: false,
    };
  }

  public render() {
    const {
      periodType, monthIndex, from, to, includeTestUsers, includeUnonboarded,
    } = this.state;
    const isWeeks = periodType === 'weeks';
    const isCustom = periodType === 'custom';
    const months = generateMonths(this.props.timezone);
    const items = isWeeks
      ? this.getWeekItems(months)
      : isCustom ? [] : months;
    const hasValidRange = from !== '' && to !== ''
      && (isWeeks ? Number(from) <= Number(to) : true);
    const hasValidCustomRange = from !== '' && to !== '' && from <= to;
    const isGenerateDisabled = isCustom ? !hasValidCustomRange : !hasValidRange;

    return (
      <div className='simple-date-range-pick-and-run pulse-picker-container'>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className='btn-group' role='group' aria-label='Pulse period type' style={{ marginRight: '5px' }}>
            {PULSE_PERIOD_TYPES.map(({ type, label }) => (
              <button
                key={type}
                type='button'
                className={`btn jr-btn${periodType === type ? ' active' : ''}`}
                aria-pressed={periodType === type}
                onClick={() => this.handlePeriodTypeClick(type)}
              >
                {label}
              </button>
            ))}
          </div>

          {isWeeks && (
            <>
              <select
                value={monthIndex}
                onChange={this.handleMonthIndexChange}
                className={
                  'simple-date-range-pick-and-run__select '
                  + 'simple-date-range-pick-and-run__select--period '
                  + 'pulse-picker'
                }
              >
                {months.map((item, idx) => (
                  <option key={idx} value={String(idx)}>{item.label}</option>
                ))}
              </select>
              <select
                value={from}
                onChange={this.handleWeekFromChange}
                className={
                  'simple-date-range-pick-and-run__select '
                  + 'simple-date-range-pick-and-run__select--from '
                  + 'pulse-picker'
                }
              >
                <option value='' disabled>Select...</option>
                {items.map((item, idx) => (
                  <option key={idx} value={String(idx)}>{item.label}</option>
                ))}
              </select>
              <span className='simple-date-range-pick-and-run__dash'>-</span>
              <select
                value={to}
                onChange={(e) => this.setState({ to: e.target.value })}
                className={
                  'simple-date-range-pick-and-run__select '
                  + 'simple-date-range-pick-and-run__select--to '
                  + 'pulse-picker'
                }
              >
                <option value='' disabled>Select...</option>
                {items
                  .map((item, idx) => ({ item, idx }))
                  .filter(({ idx }) => from === '' || idx >= Number(from))
                  .map(({ item, idx }) => (
                    <option key={idx} value={String(idx)}>{item.label}</option>
                  ))}
              </select>
            </>
          )}

          {!isWeeks && !isCustom && (
            <>
              <select
                value={from}
                onChange={this.handleMonthFromChange}
                className={
                  'simple-date-range-pick-and-run__select '
                  + 'simple-date-range-pick-and-run__select--from '
                  + 'pulse-picker'
                }
              >
                <option value='' disabled>Select...</option>
                {items.map((item, idx) => (
                  <option key={idx} value={String(idx)}>{item.label}</option>
                ))}
              </select>
              <span className='simple-date-range-pick-and-run__dash'>-</span>
              <select
                value={to}
                onChange={(e) => this.setState({ to: e.target.value })}
                className={
                  'simple-date-range-pick-and-run__select '
                  + 'simple-date-range-pick-and-run__select--to '
                  + 'pulse-picker'
                }
              >
                <option value='' disabled>Select...</option>
                {items.filter((item, idx) => from === '' || idx <= Number(from)).map((item, idx) => (
                  <option key={idx} value={String(idx)}>{item.label}</option>
                ))}
              </select>
            </>
          )}

          {isCustom && (
            <DatePickerFilter
              type='aggregateDate'
              startDate={from ? moment(from, 'YYYY-MM-DD').toDate() : undefined}
              endDate={to ? moment(to, 'YYYY-MM-DD').toDate() : undefined}
              onChange={this.handleCustomDateChange}
            />
          )}
        </div>

        <div className='pulse-picker-toggles'>
          {this.props.canShowTestUsers && (
            <label className='pulse-picker-toggle'>
              <input
                type='checkbox'
                checked={includeTestUsers}
                disabled={this.props.disabled}
                onChange={(e) => this.setState({ includeTestUsers: e.target.checked })}
              />
              Include test users
            </label>
          )}
          <label className='pulse-picker-toggle'>
            <input
              type='checkbox'
              checked={includeUnonboarded}
              disabled={this.props.disabled}
              onChange={(e) => this.setState({ includeUnonboarded: e.target.checked })}
            />
            Include un-onboarded members
          </label>
        </div>

        <button
          type='button'
          onClick={this.handleGenerate}
          disabled={isGenerateDisabled || this.props.disabled}
          className='jr-btn-default pulse-period-picker__generate'
        >
          Generate report
        </button>
      </div>
    );
  }

  private getWeekItems = (months: IDropdownItem[]): IDropdownItem[] => {
    const month = months[Number(this.state.monthIndex)] || months[0];
    return generateWeeksInMonth(this.props.timezone, month.startDate);
  };

  private handlePeriodTypeClick = (periodType: PulsePeriodType) => {
    if (periodType === this.state.periodType) {
      return;
    }
    const defaultValue = periodType === 'custom' ? '' : '0';
    this.setState({
      periodType,
      monthIndex: '0',
      from: defaultValue,
      to: defaultValue,
    });
  };

  private handleMonthIndexChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({ monthIndex: e.target.value, from: '0', to: '0' });
  };

  private handleMonthFromChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFrom = e.target.value;
    const { to } = this.state;
    // Month items are ordered most-recent-first, so higher index = older.
    const needsToReset = to === '' || Number(to) > Number(newFrom);
    this.setState({ from: newFrom, to: needsToReset ? newFrom : to });
  };

  private handleWeekFromChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFrom = e.target.value;
    const { to } = this.state;
    // Week items are ordered oldest-first, so "to" cannot be before "from".
    const needsToReset = to === '' || Number(to) < Number(newFrom);
    this.setState({ from: newFrom, to: needsToReset ? newFrom : to });
  };

  private handleCustomDateChange = ({ startDate, endDate }: { startDate?: Date; endDate?: Date }) => {
    this.setState({
      from: startDate ? moment(startDate).format('YYYY-MM-DD') : '',
      to: endDate ? moment(endDate).format('YYYY-MM-DD') : '',
    });
  };

  private handleGenerate = () => {
    const {
      periodType, from, to, includeTestUsers, includeUnonboarded,
    } = this.state;
    const runOptions: IPulseRunOptions = { includeTestUsers, includeUnonboarded };
    if (periodType === 'custom') {
      if (!from || !to || from > to) { return; }
      this.props.onGenerate({
        type: periodType,
        startDate: startOfDayInTz(from, this.props.timezone),
        endDate: endOfDayInTz(to, this.props.timezone),
      }, runOptions);
      return;
    }

    const fromIndex = Number(from);
    const toIndex = Number(to);
    if (isNaN(fromIndex) || isNaN(toIndex)) { return; }

    if (periodType === 'weeks') {
      if (fromIndex > toIndex) { return; }
      const weekItems = this.getWeekItems(generateMonths(this.props.timezone));
      this.props.onGenerate({
        type: periodType,
        startDate: weekItems[fromIndex].startDate,
        endDate: weekItems[toIndex].endDate,
      }, runOptions);
      return;
    }

    const monthItems = generateMonths(this.props.timezone);
    // Items are ordered most-recent-first, so higher index = older.
    // "from" should be the older (higher index), "to" the newer (lower index).
    const startDate = monthItems[Math.max(fromIndex, toIndex)].startDate;
    const endDate = monthItems[Math.min(fromIndex, toIndex)].endDate;
    if (startDate.getTime() > endDate.getTime()) { return; }

    this.props.onGenerate({ type: periodType, startDate, endDate }, runOptions);
  };
}
